import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { deviceAuthService } from '../api/deviceAuth';

export const DeviceLink = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const [userCode, setUserCode] = useState(() => searchParams.get('code')?.trim() ?? '');
    const [codeError, setCodeError] = useState('');
    const [approved, setApproved] = useState(false);

    useEffect(() => {
        const fromQuery = searchParams.get('code')?.trim();
        if (fromQuery) {
            setUserCode(fromQuery);
        }
    }, [searchParams]);

    const approveMutation = useMutation({
        mutationFn: (code: string) => deviceAuthService.approve(code),
        onSuccess: () => {
            setApproved(true);
            setCodeError('');
            setSearchParams({}, { replace: true });
            enqueueSnackbar(t('deviceLink.success'), { variant: 'success' });
        },
        onError: (err) => {
            if (axios.isAxiosError(err)) {
                const message =
                    (err.response?.data as { errors?: { user_code?: string[] }; message?: string } | undefined)
                        ?.errors?.user_code?.[0] ||
                    (err.response?.data as { message?: string } | undefined)?.message;
                if (message) {
                    setCodeError(message);
                    enqueueSnackbar(message, { variant: 'error' });
                    return;
                }
            }
            setCodeError(t('deviceLink.error'));
            enqueueSnackbar(t('deviceLink.error'), { variant: 'error' });
        },
    });

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const code = userCode.trim();
        if (!code) {
            setCodeError(t('validation.required'));
            return;
        }
        setCodeError('');
        approveMutation.mutate(code);
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 520, mx: 'auto' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                {t('deviceLink.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('deviceLink.subtitle')}
            </Typography>

            {approved ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                    {t('deviceLink.successDetail')}
                </Alert>
            ) : (
                <Paper
                    elevation={0}
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                        {t('deviceLink.codeLabel')}
                    </Typography>
                    <TextField
                        fullWidth
                        value={userCode}
                        onChange={(e) => {
                            setUserCode(e.target.value.toUpperCase());
                            if (codeError) {
                                setCodeError('');
                            }
                        }}
                        placeholder="ABCD-EFGH"
                        error={!!codeError}
                        helperText={codeError}
                        autoFocus
                        inputProps={{
                            autoCapitalize: 'characters',
                            autoCorrect: 'off',
                            spellCheck: false,
                            style: {
                                letterSpacing: 2,
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                fontWeight: 600,
                            },
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        }}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={approveMutation.isPending}
                        sx={{
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {approveMutation.isPending ? t('common.loading') : t('deviceLink.submit')}
                    </Button>
                </Paper>
            )}
        </Box>
    );
};
